/* ============================================================
   modals.jsx — Settings, PDF Export Preview, Auto-fill Flow
   ============================================================ */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as turf from '@turf/turf';
import { Icon, NumberField, Segment, AngleViz, Swatch, Toggle } from './ui-primitives.jsx';
import { geocodeAddress } from './data.js';
import { PG } from './geometry.js';

function ModalShell({ title, subtitle, onClose, children, footer, width = 720 }) {
  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ width, maxWidth: 'calc(100vw - 48px)' }}>
        <div className="modal-header">
          <div style={{ flex: 1 }}>
            <h2 className="modal-title">{title}</h2>
            {subtitle && <div className="modal-sub">{subtitle}</div>}
          </div>
          <button className="modal-close" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ====================================================================
// SETTINGS MODAL
// ====================================================================
export function SettingsModal({ settings, onChange, onClose, mode = 'edit', addressLabel, onBack, onConfirm }) {
  const [section, setSection] = useState('slot');
  const isCreate = mode === 'create';
  return (
    <ModalShell
      title={isCreate ? 'Neues Projekt · Einstellungen' : 'Einstellungen'}
      subtitle={
        isCreate
          ? (addressLabel
              ? `Für: ${addressLabel}`
              : 'Lege Standardwerte für die Slot-Generierung fest')
          : 'Standardwerte für neue Reihen, Gassen und Hindernisse'
      }
      onClose={onClose}
      width={820}
      footer={
        isCreate ? (
          <>
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
              Du kannst alle Werte später jederzeit ändern.
            </span>
            <div className="spacer" />
            <button className="btn" onClick={onBack}>
              <Icon name="arrow-left" size={13} /> Zurück
            </button>
            <button className="btn is-primary" onClick={onConfirm}>
              <Icon name="check" size={13} /> Projekt anlegen
            </button>
          </>
        ) : (
          <>
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
              Änderungen gelten für neue Objekte. Bestehende Reihen behalten ihre Werte.
            </span>
            <div className="spacer" />
            <button className="btn" onClick={onClose}>Schließen</button>
            <button className="btn is-primary" onClick={onClose}>Übernehmen</button>
          </>
        )
      }
    >
      <div className="settings-grid">
        <div className="settings-nav">
          {[
            { id: 'slot',    label: 'Slot-Standards',  icon: 'rows-3' },
            { id: 'lane',    label: 'Fahrgassen',       icon: 'route' },
            { id: 'label',   label: 'Beschriftung',     icon: 'type' },
            { id: 'acc',     label: 'Barrierefreiheit', icon: 'accessibility' },
            { id: 'project', label: 'Projekt',          icon: 'folder' },
            { id: 'export',  label: 'Export',           icon: 'file-output' },
          ].map(s => (
            <button
              key={s.id}
              className={`settings-nav-item ${section === s.id ? 'is-active' : ''}`}
              onClick={() => setSection(s.id)}
            >
              <Icon name={s.icon} size={13} />
              {s.label}
            </button>
          ))}
        </div>
        <div className="settings-content">
          {section === 'slot' && (
            <SettingsSection
              title="Slot-Standards"
              desc="Diese Werte werden für neu gezeichnete Reihen verwendet. EU-Standard ist 2,5 × 5,0 m bei 90°."
            >
              <SettingsFieldRow label="Slot-Breite" help="Empfehlung 2,5 m, Großfahrzeuge 2,7 m">
                <NumberField value={settings.slotWidth} onChange={(v) => onChange({ slotWidth: v })} min={2.0} max={4.0} step={0.1} />
              </SettingsFieldRow>
              <SettingsFieldRow label="Slot-Länge" help="Empfehlung 5,0 m, Längsparken 6,0 m">
                <NumberField value={settings.slotLength} onChange={(v) => onChange({ slotLength: v })} min={3.5} max={7.5} step={0.1} />
              </SettingsFieldRow>
              <SettingsFieldRow label="Standard-Winkel" help="Senkrechtparken liefert die höchste Dichte">
                <Segment
                  value={settings.defaultAngle}
                  options={[
                    { value: 90, label: '90°' },
                    { value: 60, label: '60°' },
                    { value: 45, label: '45°' },
                    { value: 0,  label: '0°' },
                  ]}
                  onChange={(v) => onChange({ defaultAngle: v })}
                />
              </SettingsFieldRow>
              <SettingsFieldRow label="Standard-Aufstellung" help="Beidseitig spart Fahrwege">
                <Segment
                  value={settings.defaultSide}
                  options={[
                    { value: 'right', label: 'Einseitig' },
                    { value: 'both',  label: 'Beidseitig' },
                  ]}
                  onChange={(v) => onChange({ defaultSide: v })}
                />
              </SettingsFieldRow>
            </SettingsSection>
          )}

          {section === 'lane' && (
            <SettingsSection
              title="Fahrgassen"
              desc="Empfohlene Mindestbreiten je nach Winkel. Der Editor warnt, wenn eine Gasse zu schmal ist."
            >
              <SettingsFieldRow label="Standardbreite (zweispurig)">
                <NumberField value={settings.laneWidthTwoWay} onChange={(v) => onChange({ laneWidthTwoWay: v })} min={3.0} max={12.0} step={0.1} />
              </SettingsFieldRow>
              <SettingsFieldRow label="Standardbreite (Einbahn)">
                <NumberField value={settings.laneWidthOneWay} onChange={(v) => onChange({ laneWidthOneWay: v })} min={3.0} max={8.0} step={0.1} />
              </SettingsFieldRow>
              <SettingsFieldRow label="Wendekreis-Radius">
                <NumberField value={settings.turningRadius} onChange={(v) => onChange({ turningRadius: v })} min={3.0} max={12.0} step={0.1} />
              </SettingsFieldRow>
              <div style={{ marginTop: 12, padding: 14, background: 'var(--panel-2)', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Referenztabelle</div>
                <table style={{ width: '100%', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                  <thead style={{ color: 'var(--text-3)' }}>
                    <tr>
                      <th style={{ textAlign: 'left', fontWeight: 500, paddingBottom: 4 }}>Winkel</th>
                      <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>min. Gasse</th>
                      <th style={{ textAlign: 'right', fontWeight: 500, paddingBottom: 4 }}>Slot/m</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { a: 90, lane: 6.0, perM: 0.40 },
                      { a: 75, lane: 5.5, perM: 0.39 },
                      { a: 60, lane: 4.8, perM: 0.35 },
                      { a: 45, lane: 4.0, perM: 0.28 },
                      { a: 30, lane: 3.5, perM: 0.20 },
                      { a: 0,  lane: 3.0, perM: 0.17 },
                    ].map(r => (
                      <tr key={r.a}>
                        <td style={{ padding: '3px 0' }}>{r.a}°</td>
                        <td style={{ textAlign: 'right' }}>{r.lane.toFixed(1)} m</td>
                        <td style={{ textAlign: 'right', color: 'var(--sand)' }}>{r.perM.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SettingsSection>
          )}

          {section === 'label' && (
            <SettingsSection
              title="Beschriftung"
              desc="Wie sollen Reihen und Lücken nummeriert werden? Diese Beschriftung erscheint im PDF-Export."
            >
              <SettingsFieldRow label="Reihen-Schema">
                <Segment
                  value={settings.rowScheme || 'A1'}
                  options={[
                    { value: 'A1',   label: 'A1, A2…' },
                    { value: 'A01',  label: 'A01' },
                    { value: '1A',   label: '1A, 2A…' },
                  ]}
                  onChange={(v) => onChange({ rowScheme: v })}
                />
              </SettingsFieldRow>
              <SettingsFieldRow label="Lücken-Label im Plan zeigen" help="Bei Zoom ≥ 19 immer einblenden">
                <Segment
                  value={settings.showLabels || 'auto'}
                  options={[
                    { value: 'always', label: 'Immer' },
                    { value: 'auto',   label: 'Auto' },
                    { value: 'never',  label: 'Nie' },
                  ]}
                  onChange={(v) => onChange({ showLabels: v })}
                />
              </SettingsFieldRow>
              <SettingsFieldRow label="Rückwärts-Einparken markieren" help="Pfeil-Symbol je Slot im PDF">
                <Toggle checked={!!settings.markBackIn} onChange={(c) => onChange({ markBackIn: c })} label="" />
              </SettingsFieldRow>
            </SettingsSection>
          )}

          {section === 'acc' && (
            <SettingsSection
              title="Barrierefreie Plätze"
              desc="Behinderten-Parkplätze sind breiter (3,5 m statt 2,5 m) und sollten in Eingangsnähe sein."
            >
              <SettingsFieldRow label="Anteil barrierefrei" help="Empfehlung: 2–5 % der Gesamtkapazität">
                <NumberField value={settings.accessiblePct} onChange={(v) => onChange({ accessiblePct: v })} min={0} max={20} step={0.5} unit="%" />
              </SettingsFieldRow>
              <SettingsFieldRow label="Slot-Breite barrierefrei">
                <NumberField value={settings.accessibleWidth} onChange={(v) => onChange({ accessibleWidth: v })} min={3.0} max={4.5} step={0.1} />
              </SettingsFieldRow>
              <SettingsFieldRow label="Eingangsnähe priorisieren">
                <Toggle checked={!!settings.accessibleNearEntry} onChange={(c) => onChange({ accessibleNearEntry: c })} label="" />
              </SettingsFieldRow>
            </SettingsSection>
          )}

          {section === 'project' && (
            <SettingsSection
              title="Projekt"
              desc="Name, Veranstaltungsdaten und Ansprechpartner für den PDF-Export."
            >
              <SettingsFieldRow label="Projektname">
                <input
                  className="field-input"
                  value={settings.projectName || ''}
                  placeholder="z. B. Sommerfest 2026"
                  onChange={(e) => onChange({ projectName: e.target.value })}
                />
              </SettingsFieldRow>
              <SettingsFieldRow label="Veranstaltungsdatum">
                <input
                  className="field-input"
                  type="date"
                  value={settings.eventDate || ''}
                  onChange={(e) => onChange({ eventDate: e.target.value })}
                />
              </SettingsFieldRow>
              <SettingsFieldRow label="Ansprechpartner Parkdienst">
                <input
                  className="field-input"
                  value={settings.contactPerson || ''}
                  placeholder="Name · Telefon"
                  onChange={(e) => onChange({ contactPerson: e.target.value })}
                />
              </SettingsFieldRow>
            </SettingsSection>
          )}

          {section === 'export' && (
            <SettingsSection
              title="Export-Optionen"
              desc="Wähle, welche Seiten im PDF enthalten sind und in welcher Auflösung."
            >
              <SettingsFieldRow label="Übersichts-Seite">
                <Toggle checked={true} onChange={() => {}} label="" />
              </SettingsFieldRow>
              <SettingsFieldRow label="Zoom-Kacheln (4 Quadranten)">
                <Toggle checked={true} onChange={() => {}} label="" />
              </SettingsFieldRow>
              <SettingsFieldRow label="Reihen-Tabelle">
                <Toggle checked={true} onChange={() => {}} label="" />
              </SettingsFieldRow>
              <SettingsFieldRow label="Einsatzbriefing-Bogen">
                <Toggle checked={true} onChange={() => {}} label="" />
              </SettingsFieldRow>
              <SettingsFieldRow label="Auflösung">
                <Segment
                  value="300"
                  options={[
                    { value: '150', label: '150 DPI' },
                    { value: '300', label: '300 DPI' },
                    { value: '600', label: '600 DPI' },
                  ]}
                  onChange={() => {}}
                />
              </SettingsFieldRow>
            </SettingsSection>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function SettingsSection({ title, desc, children }) {
  return (
    <div className="settings-section">
      <h3 className="settings-section-title">{title}</h3>
      <p className="settings-section-desc">{desc}</p>
      <div>{children}</div>
    </div>
  );
}

function SettingsFieldRow({ label, help, children }) {
  return (
    <div className="settings-field-row">
      <div>
        <div className="settings-field-label">{label}</div>
        {help && <div className="settings-field-help">{help}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ====================================================================
// PDF EXPORT PREVIEW
// ====================================================================
export function PdfExportModal({ scenario, slots, onClose, onExportPdf, onExportPng, onExportJson, getMapSnapshot, getPageOneSnapshot }) {
  const [busy, setBusy] = useState(null); // 'pdf' | 'png' | 'json' | null
  const [error, setError] = useState(null);
  // Live snapshots taken once when the modal opens. Same path the real export
  // uses — so the preview is honest about what gets exported. Two variants so
  // page 1 (vignetted) and page 5 (plain landscape) match the PDF.
  const [plainSnapshot, setPlainSnapshot] = useState(null);
  const [pageOneSnapshot, setPageOneSnapshot] = useState(null);
  useEffect(() => {
    if (!getMapSnapshot) return;
    let cancelled = false;
    getMapSnapshot()
      .then(async (snap) => {
        if (cancelled) return;
        setPlainSnapshot(snap);
        if (getPageOneSnapshot) {
          try {
            const v = await getPageOneSnapshot(snap);
            if (!cancelled) setPageOneSnapshot(v);
          } catch (err) {
            console.warn('Vignette preview failed:', err);
            if (!cancelled) setPageOneSnapshot(snap);
          }
        } else {
          setPageOneSnapshot(snap);
        }
      })
      .catch((err) => { console.warn('Preview snapshot failed:', err); });
    return () => { cancelled = true; };
  }, [getMapSnapshot, getPageOneSnapshot]);
  const pageOneMapUrl = pageOneSnapshot?.dataUrl || plainSnapshot?.dataUrl || null;
  const plainMapUrl = plainSnapshot?.dataUrl || null;

  const blocked = slots.filter(s => s.blocked).length;
  const accessible = slots.filter(s => s.accessible).length;
  const totalSlots = slots.length;
  const effective = totalSlots - blocked;
  const s = scenario.settings || {};
  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  };
  const subtitleParts = [s.projectName, fmtDate(s.eventDate), `Plan-Stand ${new Date().toLocaleDateString('de-DE')}`].filter(Boolean);

  // Group slots by row to build the table
  const rowSummary = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const r = scenario.rows.find(rw => rw.id === s.rowId);
      if (!r) continue;
      const key = r.id;
      if (!map.has(key)) {
        map.set(key, {
          rowId: r.id,
          label: r.label,
          angle: r.angle,
          side: r.side,
          slots: 0,
          blocked: 0,
          accessible: 0,
        });
      }
      const entry = map.get(key);
      entry.slots += 1;
      if (s.blocked) entry.blocked += 1;
      if (s.accessible) entry.accessible += 1;
    }
    return Array.from(map.values()).sort((a,b) => a.label.localeCompare(b.label));
  }, [slots, scenario.rows]);

  return (
    <ModalShell
      title="PDF-Export · Vorschau"
      subtitle={`${scenario.name} · Parkplatz-Mappe für das Parkleitungs-Team`}
      onClose={onClose}
      width={980}
      footer={
        <>
          <span style={{ color: error ? 'var(--danger)' : 'var(--text-3)', fontSize: 12 }}>
            {error ? (
              <><Icon name="alert-triangle" size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{error}</>
            ) : (
              <><Icon name="file-text" size={12} style={{ verticalAlign: -2, marginRight: 4 }} />5 Seiten · A4 (Querformat auf der letzten Seite)</>
            )}
          </span>
          <div className="spacer" />
          <button className="btn" onClick={onClose} disabled={!!busy}>Abbrechen</button>
          <button
            className="btn"
            disabled={!!busy}
            onClick={async () => {
              setBusy('json'); setError(null);
              try { await onExportJson?.(); } catch (e) { setError(e.message || 'JSON-Export fehlgeschlagen.'); }
              finally { setBusy(null); }
            }}
          >
            <Icon name="code" size={13} /> {busy === 'json' ? 'JSON …' : 'Als JSON'}
          </button>
          <button
            className="btn"
            disabled={!!busy}
            onClick={async () => {
              setBusy('png'); setError(null);
              try { await onExportPng?.(); } catch (e) { setError(e.message || 'PNG-Export fehlgeschlagen.'); }
              finally { setBusy(null); }
            }}
          >
            <Icon name="image" size={13} /> {busy === 'png' ? 'PNG …' : 'Als PNG'}
          </button>
          <button
            className="btn is-primary"
            disabled={!!busy}
            onClick={async () => {
              setBusy('pdf'); setError(null);
              try { await onExportPdf?.(); } catch (e) { setError(e.message || 'PDF-Export fehlgeschlagen.'); }
              finally { setBusy(null); }
            }}
          >
            <Icon name="download" size={13} /> {busy === 'pdf' ? 'PDF …' : 'PDF herunterladen'}
          </button>
        </>
      }
    >
      <div className="pdf-preview-grid">
        {/* PAGE 1 — Übersicht (mirrors export.js page 1: KPI cards + map) */}
        <div className="pdf-page">
          <div className="pdf-page-header">
            <div style={{ width: 18, height: 18, borderRadius: 3, background: '#bea78e', display: 'grid', placeItems: 'center', color: '#1a1a1c', fontFamily: 'var(--font-serif)', fontWeight: 600, fontSize: 10 }}>R</div>
            <div className="pdf-brand">Parkplatz-Plan · {scenario.name}</div>
            <div className="pdf-pagenum">Seite 1 / 5</div>
          </div>
          <div className="pdf-page-title">Übersicht</div>
          <div className="pdf-page-sub">
            {subtitleParts.length ? subtitleParts.join(' · ') : (scenario.address || '—')}
          </div>
          <div className="pdf-page-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginBottom: 8 }}>
              {[
                { label: 'Gesamtplätze', value: totalSlots },
                { label: 'Effektiv',     value: effective },
                { label: 'Blockiert',    value: blocked },
                { label: 'Barrierefrei', value: accessible },
                { label: 'Reihen',       value: scenario.rows.length },
                { label: 'Hindernisse',  value: scenario.obstacles.length },
              ].map((c, i) => (
                <div key={i} style={{ background: '#fbf6ee', border: '1px solid #d8c7ad', borderRadius: 2, padding: '4px 6px' }}>
                  <div style={{ fontSize: 7, letterSpacing: '.04em', textTransform: 'uppercase', color: '#7a6e5f' }}>{c.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1f1c19' }}>{c.value}</div>
                </div>
              ))}
            </div>
            <div className="pdf-overview-map" style={{ flex: 1 }}>
              {pageOneMapUrl
                ? <img src={pageOneMapUrl} alt="" />
                : <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#7a6e5f', fontSize: 9, fontStyle: 'italic' }}>
                    Karten-Snapshot wird erzeugt …
                  </div>}
            </div>
          </div>
        </div>

        {/* PAGE 2 — Reihen-Übersicht (row table) */}
        <div className="pdf-page">
          <div className="pdf-page-header">
            <div style={{ width: 18, height: 18, borderRadius: 3, background: '#bea78e' }} />
            <div className="pdf-brand">Parkplatz-Plan · {scenario.name}</div>
            <div className="pdf-pagenum">Seite 2 / 5</div>
          </div>
          <div className="pdf-page-title">Reihen-Übersicht</div>
          <div className="pdf-page-sub">Slot-Anzahl, Winkel und Aufstellung pro Reihe.</div>
          <div className="pdf-page-body">
            {rowSummary.length === 0 ? (
              <div style={{ color: '#7a6e5f', fontStyle: 'italic', fontSize: 9, padding: '12px 0' }}>
                Noch keine Reihen gezeichnet.
              </div>
            ) : (
              <table className="pdf-table">
                <thead>
                  <tr>
                    <th>Reihe</th>
                    <th style={{ textAlign: 'right' }}>Slots</th>
                    <th style={{ textAlign: 'right' }}>Winkel</th>
                    <th>Typ</th>
                    <th>Seiten</th>
                    <th style={{ textAlign: 'right' }}>BF</th>
                    <th style={{ textAlign: 'right' }}>Blockiert</th>
                  </tr>
                </thead>
                <tbody>
                  {rowSummary.map((r) => (
                    <tr key={r.rowId}>
                      <td style={{ fontWeight: 600 }}>{r.label}</td>
                      <td style={{ textAlign: 'right' }}>{r.slots}</td>
                      <td style={{ textAlign: 'right' }}>{Math.round(r.angle)}°</td>
                      <td>{angleNameShort(r.angle)}</td>
                      <td>{r.side === 'both' ? 'beidseitig' : r.side === 'left' ? 'links' : 'rechts'}</td>
                      <td style={{ textAlign: 'right', color: '#5fb0d9' }}>{r.accessible || '—'}</td>
                      <td style={{ textAlign: 'right', color: r.blocked > 0 ? '#a8423a' : '#7a6e5f' }}>{r.blocked || '—'}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 700 }}>Σ</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{totalSlots}</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#5fb0d9' }}>{accessible}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: blocked > 0 ? '#a8423a' : '#7a6e5f' }}>{blocked}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* PAGE 3 — Details (entries, lanes, obstacles + boundaries) */}
        <div className="pdf-page">
          <div className="pdf-page-header">
            <div style={{ width: 18, height: 18, borderRadius: 3, background: '#bea78e' }} />
            <div className="pdf-brand">Parkplatz-Plan · {scenario.name}</div>
            <div className="pdf-pagenum">Seite 3 / 5</div>
          </div>
          <div className="pdf-page-title">Details</div>
          <div className="pdf-page-sub">Ein-/Ausfahrten, Fahrgassen und Hindernisse.</div>
          <div className="pdf-page-body">
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1f1c19', marginBottom: 4 }}>Ein-/Ausfahrten</div>
            {scenario.entries?.length ? (
              <table className="pdf-table" style={{ marginBottom: 10 }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Bezeichnung</th>
                    <th style={{ textAlign: 'right' }}>Breitengrad</th>
                    <th style={{ textAlign: 'right' }}>Längengrad</th>
                  </tr>
                </thead>
                <tbody>
                  {scenario.entries.map((e, i) => (
                    <tr key={e.id}>
                      <td style={{ fontWeight: 600 }}>#{i + 1}</td>
                      <td>{e.label || 'Ein/Aus'}</td>
                      <td style={{ textAlign: 'right' }}>{e.position?.[1]?.toFixed(5)}° N</td>
                      <td style={{ textAlign: 'right' }}>{e.position?.[0]?.toFixed(5)}° E</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#7a6e5f', fontStyle: 'italic', fontSize: 9, marginBottom: 10 }}>
                Keine Ein-/Ausfahrten gesetzt.
              </div>
            )}

            <div style={{ fontSize: 10, fontWeight: 700, color: '#1f1c19', marginBottom: 4 }}>Fahrgassen</div>
            {scenario.lanes?.length ? (
              <table className="pdf-table" style={{ marginBottom: 10 }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th style={{ textAlign: 'right' }}>Breite</th>
                    <th>Richtung</th>
                  </tr>
                </thead>
                <tbody>
                  {scenario.lanes.map((l, i) => (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 600 }}>{i + 1}</td>
                      <td style={{ textAlign: 'right' }}>{(l.width ?? 0).toFixed(1)} m</td>
                      <td>{l.direction === 'one-way' ? 'Einbahn' : 'Zwei-Wege'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#7a6e5f', fontStyle: 'italic', fontSize: 9, marginBottom: 10 }}>
                Keine Fahrgassen gezeichnet.
              </div>
            )}

            <div style={{ fontSize: 10, fontWeight: 700, color: '#1f1c19', marginBottom: 4 }}>Hindernisse &amp; Flächen</div>
            <div style={{ fontSize: 9, color: '#1f1c19' }}>
              {scenario.boundaries.length} {scenario.boundaries.length === 1 ? 'Fläche' : 'Flächen'} · {scenario.obstacles.length} {scenario.obstacles.length === 1 ? 'Hindernis' : 'Hindernisse'}
            </div>
          </div>
        </div>

        {/* PAGE 4 — Waypoints table */}
        <div className="pdf-page">
          <div className="pdf-page-header">
            <div style={{ width: 18, height: 18, borderRadius: 3, background: '#bea78e' }} />
            <div className="pdf-brand">Parkplatz-Plan · {scenario.name}</div>
            <div className="pdf-pagenum">Seite 4 / 5</div>
          </div>
          <div className="pdf-page-title">Wegpunkte</div>
          <div className="pdf-page-sub">Alle 10 m entlang jeder Reihe und Fahrgasse.</div>
          <div className="pdf-page-body" style={{ overflow: 'hidden' }}>
            {(scenario.rows.length + scenario.lanes.length) === 0 ? (
              <div style={{ color: '#7a6e5f', fontStyle: 'italic', fontSize: 9 }}>
                Noch keine Reihen oder Fahrgassen vorhanden.
              </div>
            ) : (
              <div style={{ fontSize: 8, lineHeight: 1.35 }}>
                {scenario.rows.map((r) => (
                  <div key={r.id} style={{ marginBottom: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 9, color: '#1f1c19', background: '#f5ecdf', padding: '1px 5px', display: 'inline-block', borderRadius: 2, marginBottom: 2 }}>
                      Reihe {r.label}
                    </div>
                    <div style={{ color: '#7a6e5f', fontFamily: 'var(--font-mono)' }}>
                      {`${Math.max(1, Math.ceil(/* show first few */0))}`}
                      {(() => {
                        const total = (() => {
                          try {
                            const dx = (r.end[0] - r.start[0]) * 111320 * Math.cos(r.start[1] * Math.PI / 180);
                            const dy = (r.end[1] - r.start[1]) * 111320;
                            return Math.sqrt(dx * dx + dy * dy);
                          } catch { return 0; }
                        })();
                        const count = Math.max(2, Math.floor(total / 10) + 1);
                        return `${count} Wegpunkte · ${total.toFixed(1)} m`;
                      })()}
                    </div>
                  </div>
                ))}
                {scenario.lanes.map((l, i) => (
                  <div key={l.id} style={{ marginBottom: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 9, color: '#1f1c19', background: '#f5ecdf', padding: '1px 5px', display: 'inline-block', borderRadius: 2, marginBottom: 2 }}>
                      Fahrgasse {i + 1}
                    </div>
                    <div style={{ color: '#7a6e5f', fontFamily: 'var(--font-mono)' }}>
                      {(() => {
                        const total = (() => {
                          try {
                            const dx = (l.end[0] - l.start[0]) * 111320 * Math.cos(l.start[1] * Math.PI / 180);
                            const dy = (l.end[1] - l.start[1]) * 111320;
                            return Math.sqrt(dx * dx + dy * dy);
                          } catch { return 0; }
                        })();
                        const count = Math.max(2, Math.floor(total / 10) + 1);
                        return `${count} Wegpunkte · ${total.toFixed(1)} m`;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* PAGE 5 — Landscape full-page map. The A4 aspect ratio is 1/0.707
            in portrait; in landscape the ratio is 0.707/1, so we override the
            preview tile's aspect-ratio so users see the rotated orientation. */}
        <div className="pdf-page" style={{ aspectRatio: '1.4142', gridColumn: 'span 2' }}>
          <div className="pdf-page-header">
            <div style={{ width: 18, height: 18, borderRadius: 3, background: '#bea78e' }} />
            <div className="pdf-brand">Parkplatz-Plan · {scenario.name}</div>
            <div className="pdf-pagenum">Seite 5 / 5</div>
          </div>
          <div className="pdf-page-title">Lageplan</div>
          <div className="pdf-page-sub">Querformat-Übersicht für den Aushang.</div>
          <div className="pdf-page-body">
            <div className="pdf-overview-map" style={{ flex: 1 }}>
              {plainMapUrl
                ? <img src={plainMapUrl} alt="" />
                : <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#7a6e5f', fontSize: 9, fontStyle: 'italic' }}>
                    Karten-Snapshot wird erzeugt …
                  </div>}
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function angleNameShort(a) {
  if (a <= 0.5) return 'Längs';
  if (a <= 35)  return 'Flach';
  if (a <= 50)  return 'Fischgr.';
  if (a <= 70)  return 'Schräg';
  return 'Senkr.';
}

// ====================================================================
// AUTO-FILL FLOW MODAL  (stepper)
// ====================================================================
export function AutofillModal({ scenario, onClose, onApply, initialBearing, onPickBearing }) {
  const [stage, setStage] = useState(1);   // 1: select, 2: configure, 3: preview, 4: apply
  const [angle, setAngle] = useState(90);
  const [slot, setSlot] = useState({ w: 2.5, L: 5.0 });
  const [doubleSided, setDoubleSided] = useState(true);
  const [accessibleEvery, setAccessibleEvery] = useState(12);
  // Row-orientation override (degrees, 0 = N, clockwise). null means "auto"
  // — fall back to the polygon's longest-edge bearing inside autoFillPolygon.
  const [bearing, setBearing] = useState(
    initialBearing != null && Number.isFinite(initialBearing) ? initialBearing : null,
  );
  // Iteration direction — which side of the bearing gets row A.
  const [direction, setDirection] = useState('ltr');

  // Estimate count
  const boundary = scenario.boundaries[0];
  const area = boundary ? turf.area(turf.polygon([boundary.polygon])) : 0;
  const lane = PG.recommendedLaneWidth(angle);
  // crude density estimate: usable_area / module_area
  const θ = angle * Math.PI / 180;
  const rowDepth = angle <= 0.5 ? slot.w : slot.L * Math.sin(θ);
  const moduleDepth = lane + (doubleSided ? 2 * rowDepth : rowDepth);
  const slotStep = angle <= 0.5 ? slot.L : (slot.w / Math.sin(θ));
  const slotsPerSquareMeter = (doubleSided ? 2 : 1) / (slotStep * moduleDepth);
  const estCount = Math.floor(area * slotsPerSquareMeter * 0.72); // 0.72 packing factor for non-rect polygons

  return (
    <ModalShell
      title="Auto-Befüllen"
      subtitle="Dichteste gültige Aufteilung berechnen"
      onClose={onClose}
      width={840}
      footer={
        <>
          <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
            <Icon name="zap" size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
            Berechnungszeit etwa 1–2 Sekunden auf einem typischen Laptop
          </span>
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Abbrechen</button>
          <button
            className="btn is-primary"
            onClick={() => { onApply({ angle, slot, doubleSided, accessibleEvery, bearing, direction }); onClose(); }}
          >
            <Icon name="check" size={13} /> Aufteilung anwenden ({estCount} Plätze)
          </button>
        </>
      }
    >
      <div className="autofill-stages">
        {[
          { n: 1, l: 'Fläche wählen' },
          { n: 2, l: 'Winkel & Maße' },
          { n: 3, l: 'Vorschau' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.n}>
            <div className={`autofill-stage ${stage === s.n ? 'is-active' : stage > s.n ? 'is-done' : ''}`}>
              <span className="num">{stage > s.n ? '✓' : s.n}</span>
              {s.l}
            </div>
            {i < arr.length - 1 && <span className="autofill-arrow">→</span>}
          </React.Fragment>
        ))}
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ color: 'var(--sand)', fontSize: 12 }}>~ {estCount} Plätze</span>
      </div>

      <div className="autofill-body">
        <AutofillCanvas
          angle={angle}
          doubleSided={doubleSided}
          slot={slot}
        />
        <div className="autofill-side">
          <div style={{ marginBottom: 16 }}>
            <div className="insp-section-title" style={{ marginBottom: 6 }}>Fläche</div>
            <div style={{ background: 'var(--panel-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="pentagon" size={14} color="var(--obj-boundary)" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{boundary?.label || 'Festwiese Ost'}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }} className="mono">{area.toFixed(0)} m²</div>
              </div>
              <Icon name="check" size={13} color="var(--success)" />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div className="insp-section-title" style={{ marginBottom: 6 }}>Winkel</div>
            <div className="angle-presets">
              {[0, 45, 60, 75, 90].map(a => (
                <button
                  key={a}
                  className={`angle-preset ${angle === a ? 'is-active' : ''}`}
                  onClick={() => setAngle(a)}
                >
                  {a}°
                </button>
              ))}
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-3)' }}>
              {angle === 90 && 'Maximale Dichte · benötigt 6 m Gasse'}
              {angle === 75 && 'Hohe Dichte · 5,5 m Gasse'}
              {angle === 60 && 'Komfort + Dichte · 4,8 m Gasse'}
              {angle === 45 && 'Fischgräten · 4,0 m Gasse — schnellste Einparker'}
              {angle === 0 && 'Längsparken · nur 3,0 m Gasse'}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div className="insp-section-title" style={{ marginBottom: 6 }}>Reihenrichtung</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ flex: 1, fontSize: 12, fontFamily: 'var(--font-mono)', color: bearing != null ? 'var(--text)' : 'var(--text-3)' }}>
                {bearing != null ? `${bearing.toFixed(1)}°` : 'Auto (längste Kante)'}
              </div>
              {bearing != null && (
                <button
                  className="btn"
                  style={{ height: 24, padding: '0 8px', fontSize: 11 }}
                  onClick={() => setBearing(null)}
                  title="Zurück zur automatischen Ausrichtung"
                >
                  Auto
                </button>
              )}
              <button
                className="btn"
                style={{ height: 24, padding: '0 8px', fontSize: 11 }}
                onClick={() => onPickBearing?.()}
                title="Zwei Punkte auf der Karte für die Reihenrichtung"
              >
                <Icon name="compass" size={12} /> Wählen
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.4, marginBottom: 8 }}>
              Bei trapezförmigen Flächen liegt die natürliche Lauf­richtung selten auf der längsten Kante — wähle sie manuell, damit Fahrgassen erreichbar bleiben.
            </div>
            <div className="insp-row" style={{ marginBottom: 0 }}>
              <span className="insp-label">Reihenfolge</span>
              <Segment
                value={direction}
                options={[
                  { value: 'ltr', label: 'Links → Rechts' },
                  { value: 'rtl', label: 'Rechts → Links' },
                ]}
                onChange={setDirection}
              />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <div className="insp-section-title" style={{ marginBottom: 6 }}>Slot-Maße</div>
            <div className="insp-row">
              <span className="insp-label">Breite</span>
              <NumberField value={slot.w} onChange={(v) => setSlot({ ...slot, w: v })} min={2.0} max={4.0} step={0.1} />
            </div>
            <div className="insp-row">
              <span className="insp-label">Länge</span>
              <NumberField value={slot.L} onChange={(v) => setSlot({ ...slot, L: v })} min={3.5} max={7.5} step={0.1} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <Toggle
              label="Doppelreihen (Rücken-an-Rücken)"
              hint="Spart Fahrwege durch geteilte Gasse"
              checked={doubleSided}
              onChange={setDoubleSided}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <Toggle
              label="Barrierefreie Plätze einplanen"
              hint={`Jede ${accessibleEvery}. Lücke 3,5 m breit, Eingangsnähe`}
              checked={accessibleEvery > 0}
              onChange={(c) => setAccessibleEvery(c ? 12 : 0)}
            />
          </div>

          <div className="callout is-info">
            <Icon name="zap" size={13} />
            <div>
              <strong>{estCount} Plätze</strong> mit dieser Konfiguration.
              Beim Wechsel auf 45° wären es ca. <strong className="mono">{Math.floor(estCount * 0.82)}</strong>{' '}
              — aber die Gasse passt dann <em>zweimal</em> nebeneinander.
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

// Mini animated canvas showing how slots pack at the chosen angle
function AutofillCanvas({ angle, doubleSided, slot }) {
  // Use the satellite snapshot (if avail) or paint a stylised grass texture
  // Then overlay packed slots
  const W = 480, H = 360;
  const θ = angle * Math.PI / 180;
  const w = slot.w * 4;
  const L = slot.L * 4;
  const step = angle <= 0.5 ? L : (w / Math.sin(θ));
  const rowDepth = angle <= 0.5 ? w : L * Math.sin(θ);
  const lane = PG.recommendedLaneWidth(angle) * 4;
  const moduleDepth = lane + (doubleSided ? 2 * rowDepth : rowDepth);
  // Tilt the whole thing a bit to feel "on a satellite map"
  const fieldRotation = -7;

  const slots = [];
  const rows = Math.floor((H - 40) / moduleDepth);
  const cols = Math.floor((W - 40) / step);

  for (let r = 0; r < rows; r++) {
    const moduleCy = 20 + r * moduleDepth + moduleDepth / 2;
    const laneCy = moduleCy;
    // upper row top
    if (doubleSided) {
      // upper row above lane
      const rowAnchorY = laneCy - lane / 2 - rowDepth;
      for (let c = 0; c < cols; c++) {
        slots.push({
          x: 20 + c * step, y: rowAnchorY,
          orient: +1, row: r, col: c,
        });
      }
      // lower row below lane
      const rowAnchorY2 = laneCy + lane / 2;
      for (let c = 0; c < cols; c++) {
        slots.push({
          x: 20 + c * step, y: rowAnchorY2 + rowDepth,
          orient: -1, row: r, col: c, lower: true,
        });
      }
    } else {
      const rowAnchorY = moduleCy - rowDepth / 2;
      for (let c = 0; c < cols; c++) {
        slots.push({
          x: 20 + c * step, y: rowAnchorY + rowDepth,
          orient: -1, row: r, col: c,
        });
      }
    }
  }

  function slotPoly(s) {
    // s.y is the row line, s.orient = -1 means slot extends downward (+y)
    // For angle <= 0.5 we use rectangle
    if (angle <= 0.5) {
      return `${s.x},${s.y} ${s.x + L},${s.y} ${s.x + L},${s.y + s.orient * w} ${s.x},${s.y + s.orient * w}`;
    }
    const cos = Math.cos(θ);
    const sin = Math.sin(θ);
    const slant = 1;
    return `${s.x},${s.y} ${s.x + step},${s.y} ${s.x + step + slant * L * cos},${s.y + s.orient * L * sin} ${s.x + slant * L * cos},${s.y + s.orient * L * sin}`;
  }

  return (
    <div className="autofill-canvas">
      <svg className="autofill-canvas-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id="grass" patternUnits="userSpaceOnUse" width="14" height="14" patternTransform={`rotate(${fieldRotation})`}>
            <rect width="14" height="14" fill="#3b4a32" />
            <circle cx="3" cy="3" r="0.8" fill="#52623e" />
            <circle cx="9" cy="6" r="1" fill="#445436" />
            <circle cx="11" cy="11" r="0.7" fill="#566740" />
            <circle cx="2" cy="9" r="0.6" fill="#566740" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill="url(#grass)" opacity="0.85" />
        {/* Soft tan boundary roughly inset */}
        <rect x="14" y="14" width={W-28} height={H-28} fill="none" stroke="#ffd166" strokeWidth="1.4" strokeDasharray="6 4" opacity="0.75" rx="14" />
        {/* Lanes */}
        <g opacity="0.85">
          {Array.from({ length: rows }).map((_, r) => {
            const moduleCy = 20 + r * moduleDepth + moduleDepth / 2;
            return (
              <rect key={'l'+r}
                    x="20" y={moduleCy - lane/2}
                    width={W-40} height={lane}
                    fill="rgba(154,139,255,0.18)"
                    stroke="#9a8bff" strokeWidth="0.8" strokeDasharray="3 3" />
            );
          })}
        </g>
        {/* Slots */}
        <g>
          {slots.map((s, i) => (
            <polygon key={i} points={slotPoly(s)}
                     fill="rgba(190,167,142,0.55)"
                     stroke="#e0d0bc" strokeWidth="0.6" />
          ))}
        </g>
        {/* Live count indicator */}
        <g>
          <rect x={W-90} y={H-30} width="80" height="22" rx="3" fill="rgba(15,15,17,0.85)" />
          <text x={W-50} y={H-15} textAnchor="middle"
                fill="#bea78e" fontFamily="JetBrains Mono"
                fontSize="11" fontWeight="600">
            {slots.length} Plätze
          </text>
        </g>
      </svg>
    </div>
  );
}

// ====================================================================
// ADDRESS MODAL — geocode an address to start a new scenario / move map
// ====================================================================
export function AddressModal({ mode = 'create', initialQuery = '', onClose, onPick }) {
  // mode: 'create'  → "Neuen Standort anlegen"  (default)
  //       'change'  → "Standort des aktuellen Projekts ändern"
  const [query, setQuery] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null); // null = nothing tried yet; [] = no hits
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  // focus + select on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  async function doSearch(e) {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setBusy(true);
    setError(null);
    setResults(null);
    try {
      const hits = await geocodeAddress(q, { limit: 7, lang: 'de' });
      setResults(hits);
      setSelectedIdx(0);
      if (hits.length === 0) {
        setError('Keine Treffer. Versuche es genauer (Straße + Hausnummer + Ort).');
      }
    } catch (err) {
      console.error(err);
      setError('Adresssuche nicht erreichbar. Prüfe die Internetverbindung.');
    } finally {
      setBusy(false);
    }
  }

  function commit(hit) {
    if (!hit) return;
    // The parent's onPick decides what modal opens next (settings step for a
    // new project, or just close for an address change). Don't call onClose
    // here — it would race with that decision and could blow away a freshly
    // opened follow-up modal.
    onPick({
      lat: hit.lat,
      lng: hit.lng,
      name: hit.shortLabel || hit.label.split(',').slice(0, 2).join(',').trim(),
      address: hit.label,
      zoom: hit.category === 'highway' || hit.type === 'residential' ? 19 : 18,
    });
  }

  function onKeyDown(e) {
    if (!results || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx((i) => Math.min(results.length - 1, i + 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx((i) => Math.max(0, i - 1)); }
    if (e.key === 'Enter' && results[selectedIdx]) { e.preventDefault(); commit(results[selectedIdx]); }
  }

  const title = mode === 'change' ? 'Standort ändern' : 'Standort des Parkplatzes';
  const subtitle = mode === 'change'
    ? 'Adresse eingeben, um die Karte auf einen anderen Ort zu bewegen.'
    : 'Adresse eingeben, um den Parkplatz an einem realen Ort zu planen.';

  return (
    <ModalShell title={title} subtitle={subtitle} onClose={onClose} width={620}>
      <div className="address-modal">
        <form className="address-search" onSubmit={doSearch}>
          <div className="address-input-wrap">
            <span className="address-input-icon"><Icon name="map-pin" size={14} /></span>
            <input
              ref={inputRef}
              className="address-input"
              type="text"
              placeholder='z.B. "Hauptstraße 12, 80331 München" oder "Theresienwiese"'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              autoComplete="off"
              spellCheck="false"
            />
            {query && (
              <button
                type="button"
                className="address-clear"
                onClick={() => { setQuery(''); setResults(null); setError(null); inputRef.current?.focus(); }}
                title="Eingabe löschen"
              >
                <Icon name="x" size={12} />
              </button>
            )}
          </div>
          <button type="submit" className="btn is-primary" disabled={busy || !query.trim()}>
            {busy
              ? <><Icon name="loader" size={13} /> Suche…</>
              : <><Icon name="search" size={13} /> Suchen</>
            }
          </button>
        </form>

        {error && (
          <div className="callout" style={{ marginBottom: 12 }}>
            <Icon name="alert-triangle" size={13} />
            <div>{error}</div>
          </div>
        )}

        {results === null && !busy && !error && (
          <div className="address-empty">
            <Icon name="search" size={22} />
            <div className="address-empty-title">Wo soll der Parkplatz entstehen?</div>
            <p>
              Gib eine Adresse, einen Straßennamen oder ein POI (z.B. Bahnhof, Stadion)
              ein. Auf der gewählten Stelle siehst Du Satellitenbilder und kannst dort
              Flächen, Reihen, Fahrgassen und Hindernisse einzeichnen.
            </p>
            <div className="address-tips">
              <div>·  Straße + Hausnummer + PLZ ist am genauesten</div>
              <div>·  Auch Koordinaten möglich: <span className="mono">48.1320, 11.5510</span></div>
              <div>·  Die Suche nutzt OpenStreetMap-Daten</div>
            </div>
          </div>
        )}

        {busy && (
          <div className="address-empty">
            <div className="address-spinner" />
            <div className="address-empty-title">Adresse wird gesucht…</div>
          </div>
        )}

        {results && results.length > 0 && (
          <div className="address-results">
            <div className="address-results-header">
              {results.length} Treffer · Pfeiltasten zum Wählen · <span className="kbd">↵</span> bestätigen
            </div>
            <ul className="address-list">
              {results.map((r, i) => (
                <li
                  key={i}
                  className={`address-item ${i === selectedIdx ? 'is-selected' : ''}`}
                  onMouseEnter={() => setSelectedIdx(i)}
                  onClick={() => commit(r)}
                >
                  <span className="address-item-pin"><Icon name="map-pin" size={13} /></span>
                  <div className="address-item-text">
                    <div className="address-item-primary">{r.shortLabel || r.label.split(',').slice(0, 3).join(',')}</div>
                    <div className="address-item-secondary">{r.label}</div>
                  </div>
                  <span className="address-item-coords mono">
                    {r.lat.toFixed(4)}, {r.lng.toFixed(4)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

